/**
 * ═══════════════════════════════════════════════════════════════════════
 * 🔒🔒🔒  ARCHIVO BLOQUEADO POR ORDEN DEL CREADOR  🔒🔒🔒
 * ═══════════════════════════════════════════════════════════════════════
 * NO MODIFICAR sin autorización explícita del usuario.
 * Para desbloquear, el usuario debe identificarse como creador
 * (ej. "yo soy el creador"). Ver: /app/memory/LOCKED_FILES.md
 * ═══════════════════════════════════════════════════════════════════════
 *
 * 🟦 CUADRO NEGRO #1 — Modal universal del botón X.
 *
 * Componente compartido para Builder, Marketplace, Workspace y Mis Apps.
 * Ofrece 3 acciones según el Cuadro Negro:
 *   1. Guardar y salir   → `onSaveAndExit()` (puede ser async)
 *   2. Salir sin guardar → `onExitNoSave()`
 *   3. Cancelar          → cierra el modal y vuelve al módulo
 *
 * Si `hasUnsavedChanges === false`, el caller puede omitir el modal y
 * navegar directo. Cuando hay cambios sin guardar, el padre lo invoca.
 *
 * Props:
 *   - open: boolean
 *   - onCancel: () => void   (cierra el modal sin salir)
 *   - onSaveAndExit: () => Promise<void> | void   (opcional; si no se pasa,
 *       el botón "Guardar y salir" queda deshabilitado)
 *   - onExitNoSave: () => void   (sale del módulo sin persistir cambios)
 *   - title?: string
 *   - description?: string
 *   - es?: boolean
 *   - testIdPrefix?: string   (default "exit-confirm")
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, LogOut, Loader2 } from 'lucide-react';

export default function ExitConfirmModal({
  open,
  onCancel,
  onSaveAndExit,
  onExitNoSave,
  title,
  description,
  es = true,
  testIdPrefix = 'exit-confirm',
}) {
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSaveAndExit = async () => {
    if (!onSaveAndExit || saving) return;
    setSaving(true);
    try {
      await onSaveAndExit();
    } finally {
      setSaving(false);
    }
  };

  const resolvedTitle = title || (es ? '¿Querés salir?' : 'Are you sure you want to leave?');
  const resolvedDesc = description || (es
    ? 'Tenés cambios sin guardar. Elegí cómo continuar.'
    : 'You have unsaved changes. Choose how to continue.');

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`${testIdPrefix}-overlay`}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel?.(); }}
      className="fixed inset-0 z-[2000000] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="w-full max-w-sm bg-slate-950 border border-fuchsia-500/40 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 bg-gradient-to-r from-fuchsia-950/60 to-cyan-950/40 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">{resolvedTitle}</h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            data-testid={`${testIdPrefix}-x`}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white disabled:opacity-50"
            aria-label={es ? 'Cancelar' : 'Cancel'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-slate-300 mb-5">{resolvedDesc}</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleSaveAndExit}
              disabled={!onSaveAndExit || saving}
              data-testid={`${testIdPrefix}-save-and-exit`}
              className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 disabled:opacity-40 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {es ? 'Guardar y salir' : 'Save and exit'}
            </button>
            <button
              type="button"
              onClick={onExitNoSave}
              disabled={saving}
              data-testid={`${testIdPrefix}-exit-no-save`}
              className="w-full px-4 py-2.5 rounded-lg bg-red-900/40 hover:bg-red-800/60 text-red-200 hover:text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <LogOut className="w-4 h-4" />
              {es ? 'Salir sin guardar' : 'Exit without saving'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              data-testid={`${testIdPrefix}-cancel`}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-sm transition-colors disabled:opacity-50"
            >
              {es ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
